import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { deliveryZoneApi } from '@app/store';
import { IDeliveryZone, DeliveryZoneDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { DeliveryZoneForm } from './DeliveryZoneForm';
import { DeliveryZoneFormValues, transformToUpdatableFormDeliveryZone } from './delivery-zone-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { DeliveryZoneCard } from './DeliveryZoneCard';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { useAppDispatch } from '@app/store/hooks';

interface DeliveryZoneUpdateProps {
  deliveryZone?: Partial<IDeliveryZone>;
  isFetching: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (deliveryZone: Partial<IDeliveryZone>) => void;
}

export const DeliveryZoneUpdate = ({ deliveryZone, isFetching, toggle, onDelete }: DeliveryZoneUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const dispatch = useAppDispatch();

  const [update, { isLoading, error: apiErrors }] = deliveryZoneApi.useUpdateOneDeliveryZonesMutation();

  const formMethods = useForm<DeliveryZoneFormValues>({
    resolver: classValidatorResolver(DeliveryZoneDTO),
    defaultValues: transformToUpdatableFormDeliveryZone(deliveryZone)
  });

  const onUpdate = async (formValues: Partial<IDeliveryZone>) => {
    try {
      const updatedDeliveryZone = await update({ _id: deliveryZone?._id!, ...formValues }).unwrap();

      // Rafraîchir explicitement la liste des zones après édition
      dispatch(
        deliveryZoneApi.util.invalidateTags([{ type: 'DeliveryZones', id: 'LIST' }])
      );

      triptych.close({ mode: 'UPDATE', data: updatedDeliveryZone });

      toast.success('Votre zone de livraison a bien été modifiée.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre zone de livraison.')
    }
  }

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormDeliveryZone(deliveryZone));
  }, [deliveryZone, formMethods.reset]);

  return (
    <Fragment>
      <DeliveryZoneCard
        deliveryZone={deliveryZone}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>

          <Panel.FormContainer>

            <CheckPermissions permissions={['dashboard::delivery-zones.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  disabled={!deliveryZone}
                  bordered
                  important
                  onPress={() => onDelete(deliveryZone!)} />
              </Panel.Header>
            </CheckPermissions>

            <DeliveryZoneForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};

